import { useState, useCallback, useEffect, useRef } from 'react';
import { useApolloClient, useMutation, useQuery } from 'react-apollo';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import moment from 'moment';
import { format } from 'date-fns';

import { checkout as checkoutMutation } from '../../../../graphql/mutations/checkout';
import { purchasedEventTransferCreate as purchasedEventTransferCreateMutation } from '../../../../graphql/mutations/purchased-events';
import { openAlert } from '../../../../actions/modal-actions';
import { getErrorString } from '../../../../util/normalize-errors';
import { sentryCaptureMessage } from '../../../../util/sentry';
import {
  EventItems,
  MeForPurchasedEvents,
} from '../../../../graphql/queries/events';
import {
  accountLoginByPhone as accountLoginByPhoneMutation,
  accountUpdate as accountUpdateMutation,
  phoneVerificationRequest as phoneVerificationRequestMutation,
} from '../../../../graphql/mutations/account';
import { MeQuery } from '../../../../graphql/queries/account';
import { loginSuccess } from '../../../../actions/auth-actions';
import useBasename from '../../../../components/routing/useBasename';
import useAuthenticated from '../../../../hooks/useAuthenticated';
import { ALBUM_SUBTYPE } from '../../../../constants/albums';
import { WEDDINGS_GIFT_STEPS } from './constants';
import { isWeddingsContent } from '../../../../util/album';

const useWeddingsGift = () => {
  const location = useLocation();
  const basename = useBasename();
  const history = useHistory();
  const authenticated = useAuthenticated();
  const userId = useRef(null);
  const [step, setStep] = useState(WEDDINGS_GIFT_STEPS.DETAILS);
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [eventItem, setEventItem] = useState('');
  const [purchasedEvent, setPurchasedEvent] = useState(null);
  const dispatch = useDispatch();
  const onErrorModalOpen = useCallback(state => dispatch(openAlert(state)), [
    dispatch,
  ]);
  const onLogin = useCallback(() => dispatch(loginSuccess()));
  const {
    data: { eventItems = [] },
  } = useQuery(EventItems);
  const {
    data: { me: { purchased_events: purchasedEvents = {} } = {} } = {},
  } = useQuery(MeForPurchasedEvents, {
    fetchPolicy: 'cache-and-network',
    variables: {
      unassigned_only: true,
    },
    skip: !authenticated || step !== WEDDINGS_GIFT_STEPS.SETUP_GALLERY,
  });
  const [phoneVerificationRequest] = useMutation(
    phoneVerificationRequestMutation,
  );
  const [accountLoginByPhone] = useMutation(accountLoginByPhoneMutation);
  const [accountUpdate] = useMutation(accountUpdateMutation);
  const [purchasedEventTransferCreate] = useMutation(
    purchasedEventTransferCreateMutation,
  );
  const [checkout] = useMutation(checkoutMutation);
  const client = useApolloClient();

  const { promo_code: defaultPromoCode } =
    qs.parse(location.search ?? '') ?? {};

  useEffect(() => {
    if (purchasedEvents?.records?.length > 0) {
      const weddingPurchasedEvents = purchasedEvents.records.filter(item =>
        isWeddingsContent(item),
      );
      const [recentlyPurchasedEvent] = weddingPurchasedEvents;
      if (recentlyPurchasedEvent) {
        setPurchasedEvent(recentlyPurchasedEvent);
      }
    }
  }, [purchasedEvents?.records?.length]);

  const onPhoneVerificationRequest = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      await phoneVerificationRequest({
        variables: {
          input: {
            phone_number: values.phone_number,
            intent: 'LOGIN',
            platform: 'WEB',
            login_only: false,
          },
        },
      });
      setStep(WEDDINGS_GIFT_STEPS.PIN_VERIFY);
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onPinVerify = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      await accountLoginByPhone({
        variables: {
          input: {
            phone_number: values.phone_number,
            phone_verification_code: values.code,
            set_cookie: true,
          },
        },
      });

      const {
        data: { me: user },
      } = await client.query({
        query: MeQuery,
        fetchPolicy: 'network-only',
      });

      userId.current = user.id;

      await accountUpdate({
        variables: {
          input: {
            id: user.id,
            name: `${values.first_name} ${values.last_name}`.trim(),
            client_store_items: [
              {
                key: 'shutterbug_signup_timestamp',
                value: moment().valueOf().toString(),
              },
            ],
            email_address: values.email_address,
            state_id: values.state_id ?? undefined,
          },
        },
      });

      onLogin();

      // Redirect the user to the new setup.
      history.push({
        pathname: `/shutterbug/weddings/gift/configure`,
        search: `?eventType=${ALBUM_SUBTYPE.WEDDINGS}&eventDate=${
          values.wedding_date ? format(values.wedding_date, 'MM/dd/yyyy') : ''
        }&brideName=${values.bride_name}&groomName=${values.groom_name}`,
      });
      // setStep(WEDDINGS_GIFT_STEPS.SKU_SELECTION);
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onPinCodeResend = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      await phoneVerificationRequest({
        variables: {
          input: {
            phone_number: values.phone_number,
            intent: 'LOGIN',
            platform: 'WEB',
            login_only: false,
          },
        },
      });
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error sending the PIN.',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onEventItemSelected = type => {
    setEventItem(type);
    setStep(WEDDINGS_GIFT_STEPS.CHECKOUT);
  };

  const onTransferEvent = async values => {
    if (loading || !purchasedEvent) {
      return;
    }

    try {
      setLoading(true);
      await purchasedEventTransferCreate({
        variables: {
          input: {
            purchased_event_id: purchasedEvent?.id,
            target_phone_number: values.couples_phone_number,
            target_email_address: values.couple_email_address,
          },
        },
      });

      setFormValues(values);
      setStep(WEDDINGS_GIFT_STEPS.SEND_GIFT_NOW_SUCCESS);
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error during we sending your gift',
        info: responseError?.request_id ?? getErrorString(responseError),
      });
    } finally {
      setLoading(false);
    }
  };

  const onConfigureGallery = async () => {
    if (loading) {
      return;
    }

    if (purchasedEvent) {
      history.push(
        `/app/${basename}/albums/create?eventType=${ALBUM_SUBTYPE.WEDDINGS}&purchasedEventId=${purchasedEvent.id}`,
      );
    }
  };

  const onSendGiftNow = async () => {
    if (loading) {
      return;
    }

    setStep(WEDDINGS_GIFT_STEPS.SEND_GIFT_NOW);
  };

  const onCheckout = async values => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      await checkout({
        variables: {
          input: {
            account_id: userId.current,
            email_address: values.stripe_token?.email,
            stripe_token: values.stripe_token?.id,
            event_items: [
              {
                event_item_id: values.event_item_id,
                for_gift: true,
              },
            ],
          },
        },
      });

      setFormValues(values);
      setStep(WEDDINGS_GIFT_STEPS.SETUP_GALLERY);
      setLoading(false);
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your purchase',
        info: responseError?.request_id ?? getErrorString(responseError),
      });

      sentryCaptureMessage(
        'WEDDINGS GIFT PURCHASE - Purchase Error - Checkout',
        {
          level: 'error',
          extra: {
            error: responseError,
          },
        },
      );
      setLoading(false);
    }
  };

  const canGoBack = (() => {
    switch (step) {
      case WEDDINGS_GIFT_STEPS.PIN_VERIFY:
      case WEDDINGS_GIFT_STEPS.SEND_GIFT_NOW:
        return true;
      default:
        return false;
    }
  })();

  const onGoBack = () => {
    switch (step) {
      case WEDDINGS_GIFT_STEPS.PIN_VERIFY:
        setStep(WEDDINGS_GIFT_STEPS.DETAILS);

        break;
      case WEDDINGS_GIFT_STEPS.SEND_GIFT_NOW:
        setStep(WEDDINGS_GIFT_STEPS.SETUP_GALLERY);

        break;
      default:
        break;
    }
  };

  return {
    step,
    steps: [WEDDINGS_GIFT_STEPS.DETAILS, WEDDINGS_GIFT_STEPS.PIN_VERIFY],
    setStep,
    onPhoneVerificationRequest,
    onPinVerify,
    onPinCodeResend,
    onEventItemSelected,
    onConfigureGallery,
    onSendGiftNow,
    onTransferEvent,
    onCheckout,
    loading,
    eventItems,
    formValues,
    canGoBack,
    onGoBack,
    defaultPromoCode,
    eventItem,
  };
};

export default useWeddingsGift;
